import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  TextField,
  Button,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  Send as SendIcon,
  WhatsApp as WhatsAppIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
} from '@mui/icons-material';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    // TODO: Implement form submission logic
    console.log('Form submitted:', formData);
    setSubmitted(true);
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: '',
    });
  };

  const contactInfo = [
    {
      icon: <PhoneIcon sx={{ fontSize: 30, color: 'primary.main' }} />,
      title: 'Téléphone',
      primary: '+221 77 123 4567',
      secondary: 'Appelez-nous pour toute question',
      action: 'tel:+221771234567',
    },
    {
      icon: <WhatsAppIcon sx={{ fontSize: 30, color: 'primary.main' }} />,
      title: 'WhatsApp',
      primary: '+221 77 123 4567',
      secondary: 'Message rapide via WhatsApp',
      action: 'https://wa.me/221771234567',
    },
    {
      icon: <EmailIcon sx={{ fontSize: 30, color: 'primary.main' }} />,
      title: 'Email',
      primary: 'info@totalbat.com',
      secondary: 'Envoyez-nous un email',
      action: 'mailto:info@totalbat.com',
    },
    {
      icon: <LocationIcon sx={{ fontSize: 30, color: 'primary.main' }} />,
      title: 'Adresse',
      primary: 'Dakar, Sénégal',
      secondary: 'Zone Industrielle de Thiaroye',
      action: null,
    },
  ];

  const businessHours = [
    { day: 'Lundi - Vendredi', hours: '8:00 - 18:00' },
    { day: 'Samedi', hours: '8:00 - 16:00' },
    { day: 'Dimanche', hours: 'Fermé' },
  ];

  const socialMedia = [
    { name: 'Facebook', icon: <FacebookIcon />, url: 'https://facebook.com/totalbat' },
    { name: 'Twitter', icon: <TwitterIcon />, url: 'https://twitter.com/totalbat' },
    { name: 'Instagram', icon: <InstagramIcon />, url: 'https://instagram.com/totalbat' },
    { name: 'LinkedIn', icon: <LinkedInIcon />, url: 'https://linkedin.com/company/totalbat' },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Hero Section */}
      <Box sx={{ textAlign: 'center', mb: 8 }}>
        <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Contactez-Nous
        </Typography>
        <Typography variant="h5" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
          Notre équipe est là pour vous accompagner dans tous vos projets de construction
        </Typography>
      </Box>

      <Grid container spacing={6}>
        {/* Contact Form */}
        <Grid item xs={12} lg={7}>
          <Paper sx={{ p: 4 }}>
            <Typography variant="h4" component="h2" gutterBottom>
              Envoyez-nous un message
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Remplissez le formulaire ci-dessous et nous vous répondrons dans les plus brefs délais.
            </Typography>

            {submitted && (
              <Alert severity="success" sx={{ mb: 3 }}>
                Votre message a été envoyé avec succès ! Nous vous répondrons dans les plus brefs délais.
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nom complet"
                    value={formData.name}
                    onChange={handleInputChange('name')}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange('email')}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Téléphone"
                    value={formData.phone}
                    onChange={handleInputChange('phone')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Sujet"
                    value={formData.subject}
                    onChange={handleInputChange('subject')}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Message"
                    multiline
                    rows={6}
                    value={formData.message}
                    onChange={handleInputChange('message')}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    startIcon={<SendIcon />}
                    sx={{ minWidth: 200 }}
                  >
                    Envoyer le message
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>

        {/* Contact Information */}
        <Grid item xs={12} lg={5}>
          <Stack spacing={4}>
            {/* Contact Info Cards */}
            <Box>
              <Typography variant="h5" component="h2" gutterBottom>
                Informations de contact
              </Typography>
              <Stack spacing={2}>
                {contactInfo.map((info, index) => (
                  <Card key={index} sx={{ p: 2 }}>
                    <ListItem disablePadding>
                      <ListItemIcon>
                        {info.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          info.action ? (
                            <Button
                              component="a"
                              href={info.action}
                              sx={{ p: 0, minWidth: 'auto', textTransform: 'none', color: 'inherit' }}
                            >
                              {info.primary}
                            </Button>
                          ) : (
                            info.primary
                          )
                        }
                        secondary={info.secondary}
                      />
                    </ListItem>
                  </Card>
                ))}
              </Stack>
            </Box>

            {/* Business Hours */}
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ScheduleIcon sx={{ fontSize: 30, color: 'primary.main', mr: 2 }} />
                  <Typography variant="h6">
                    Heures d'ouverture
                  </Typography>
                </Box>
                <List dense>
                  {businessHours.map((schedule, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={schedule.day}
                        secondary={schedule.hours}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>

            {/* Social Media */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Suivez-nous
                </Typography>
                <Stack direction="row" spacing={1}>
                  {socialMedia.map((social, index) => (
                    <Button
                      key={index}
                      component="a"
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outlined"
                      size="small"
                      startIcon={social.icon}
                      sx={{ flex: 1 }}
                    >
                      {social.name}
                    </Button>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      {/* Map Section */}
      <Box sx={{ mt: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h4" component="h2" gutterBottom>
            Notre Localisation
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Visitez notre entrepôt pour voir nos produits en personne
          </Typography>
          
          {/* Placeholder for map */}
          <Box
            sx={{
              height: 400,
              bgcolor: 'grey.200',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 2,
              mb: 3,
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <LocationIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Carte Interactive
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Zone Industrielle de Thiaroye, Dakar, Sénégal
              </Typography>
            </Box>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              startIcon={<LocationIcon />}
              component="a"
              href="https://maps.google.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              Voir sur Google Maps
            </Button>
            <Button
              variant="outlined"
              startIcon={<PhoneIcon />}
              component="a"
              href="tel:+221771234567"
            >
              Appeler pour directions
            </Button>
          </Stack>
        </Paper>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ mt: 8 }}>
        <Typography variant="h4" component="h2" align="center" gutterBottom>
          Questions Fréquentes
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Comment commander des matériaux ?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Vous pouvez commander en ligne via notre site web, par téléphone au +221 77 123 4567, 
                  ou visiter notre entrepôt directement.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quels sont les délais de livraison ?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Livraison le même jour pour les commandes passées avant 14h. 
                  Pour les commandes en gros, délais selon disponibilité.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Acceptez-vous les commandes en gros ?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Oui, nous proposons des prix spéciaux pour les commandes en gros. 
                  Contactez-nous pour un devis personnalisé.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quels sont vos moyens de paiement ?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Nous acceptons les paiements en espèces, par carte bancaire, 
                  virement bancaire et mobile money (Orange Money, Wave, etc.).
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default Contact; 